import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CircularProgress,
  Link,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Smartphone, ArrowLeft } from 'lucide-react';
import PublicHeader from '../../components/Layout/PublicHeader';

export const UserLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    setIsLoading(true);
    setError(null);

    // TODO: Implement actual user login via API
    setTimeout(() => {
      setIsLoading(false);
      setError('User web login is coming soon. Please download the HUSLRZ app for the full experience.');
    }, 1000);
  };

  return (
    <>
      <Helmet>
        <title>User Login — HUSLRZ</title>
      </Helmet>
      <PublicHeader activeLink="login" />
      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 60px)',
            py: { xs: 3, sm: 0 },
          }}
        >
          <Card
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              boxShadow: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}
            >
              User Login
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              Access your HUSLRZ account to book fitness sessions
            </Typography>

            {/* Download App CTA */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)',
                borderRadius: 2,
                p: { xs: 2.5, sm: 3 },
                mb: 3,
                textAlign: 'center',
                color: 'white',
              }}
            >
              <Smartphone size={28} style={{ marginBottom: 8 }} />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Best on Mobile
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Download the HUSLRZ app for the best booking experience with GPS check-in, real-time notifications, and more.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => alert('APK download coming soon! Stay tuned.')}
                sx={{
                  backgroundColor: 'white',
                  color: '#14B8A6',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#f0fdfa' },
                }}
              >
                Download APK
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                OR LOGIN ON WEB
              </Typography>
            </Divider>

            {error && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                }}
              >
                <Typography variant="body2" color="error" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {error}
                </Typography>
              </Box>
            )}

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  height: '48px',
                  fontSize: { xs: '1rem', sm: '1rem' },
                  fontWeight: 600,
                  backgroundColor: '#14B8A6',
                  '&:hover': { backgroundColor: '#0F9688' },
                }}
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login'}
              </Button>
            </form>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                Don't have an account?{' '}
                <Link
                  onClick={() => navigate('/signup')}
                  sx={{
                    cursor: 'pointer',
                    color: '#14B8A6',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Sign up here
                </Link>
              </Typography>
              <Button
                variant="text"
                startIcon={<ArrowLeft size={16} />}
                onClick={() => navigate('/login')}
                sx={{ mt: 1, color: '#5f6368', textTransform: 'none', fontSize: '0.85rem' }}
              >
                Back to login options
              </Button>
            </Box>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default UserLoginPage;
